/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Moon, Sun, Globe, Accessibility } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuPortal,
} from '@/components/ui/dropdown-menu';
import { useAccessibility } from '@/lib/accessibility';
import { useTheme } from '@/lib/theme';
import { AVAILABLE_LANGUAGES } from '@/i18n';

/**
 * Small top-bar controls (theme / language / senior mode) in both desktop and
 * overflow-menu variants. Extracted from AppShell.jsx (Code Quality refactor).
 */

const useLanguage = () => {
  const { i18n } = useTranslation();
  const change = (code) => { i18n.changeLanguage(code); localStorage.setItem('sc_lang', code); };
  return { i18n, change };
};

export const LanguageSwitcher = () => {
  const { i18n, change } = useLanguage();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="h-8 px-2 text-xs font-semibold uppercase" data-testid="language-switcher-trigger">
          {i18n.language.toUpperCase()}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="max-h-[70vh] overflow-y-auto">
        {AVAILABLE_LANGUAGES.map((l) => (
          <DropdownMenuItem key={l.code} onClick={() => change(l.code)}
            data-testid={`language-switcher-item-${l.code}`}
            className={i18n.language === l.code ? 'font-semibold text-primary' : ''}>
            <span className="mr-2 text-base">{l.flag}</span>{l.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const ThemeToggle = () => {
  const { theme, setTheme } = useTheme();
  const next = theme === 'dark' ? 'light' : 'dark';
  return (
    <button
      onClick={() => setTheme(next)}
      className="h-9 w-9 rounded-full hover:bg-secondary flex items-center justify-center transition"
      data-testid="theme-toggle-topbar"
      title={theme === 'dark' ? 'Light mode' : 'Dark mode'}
    >
      {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
    </button>
  );
};

// ---- Overflow menu items (used only on < lg viewports) --------------------

export const OverflowThemeItem = () => {
  const { theme, setTheme } = useTheme();
  const isDark = theme === 'dark';
  return (
    <DropdownMenuItem
      onClick={(e) => { e.preventDefault(); setTheme(isDark ? 'light' : 'dark'); }}
      data-testid="overflow-theme"
    >
      {isDark ? <Sun className="h-4 w-4 mr-2" /> : <Moon className="h-4 w-4 mr-2" />}
      {isDark ? 'Light mode' : 'Dark mode'}
    </DropdownMenuItem>
  );
};

export const OverflowSeniorModeItem = () => {
  const a11y = useAccessibility();
  const on = !!a11y.elderlyMode;
  const toggle = (e) => {
    e.preventDefault();
    a11y.setElderlyMode(!on);
    a11y.setHighContrast(!on);
    a11y.setTextScale(on ? 100 : 150);
  };
  return (
    <DropdownMenuItem onClick={toggle} data-testid="overflow-senior-mode">
      <Accessibility className="h-4 w-4 mr-2" />
      {on ? 'Disattiva modalità senior' : 'Modalità senior'}
    </DropdownMenuItem>
  );
};

export const OverflowLanguageItem = () => {
  const { i18n, change } = useLanguage();
  const current = AVAILABLE_LANGUAGES.find((l) => l.code === i18n.language) || AVAILABLE_LANGUAGES[0];
  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger data-testid="overflow-language-trigger">
        <Globe className="h-4 w-4 mr-2" />
        <span className="flex-1">Lingua</span>
        <span className="text-xs font-semibold uppercase text-muted-foreground">{current?.code}</span>
      </DropdownMenuSubTrigger>
      <DropdownMenuPortal>
        <DropdownMenuSubContent className="max-h-[60vh] overflow-y-auto">
          {AVAILABLE_LANGUAGES.map((l) => (
            <DropdownMenuItem
              key={l.code}
              onClick={() => change(l.code)}
              data-testid={`overflow-language-${l.code}`}
              className={i18n.language === l.code ? 'font-semibold text-primary' : ''}
            >
              <span className="mr-2 text-base">{l.flag}</span>{l.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuSubContent>
      </DropdownMenuPortal>
    </DropdownMenuSub>
  );
};
